import { heicTo, isHeic } from 'heic-to';

const MAX_BYTES = 1 * 1024 * 1024;
const MAX_DIMENSION = 1568;
const QUALITY_STEPS = [0.85, 0.7, 0.55, 0.4];

const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to decode image'));
    img.src = url;
  });
};

const loadBitmap = async (file: File): Promise<HTMLImageElement | ImageBitmap> => {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      // Fall through to <img> loader
    }
  }

  const url = URL.createObjectURL(file);

  try {
    return await loadImage(url);
  } finally {
    URL.revokeObjectURL(url);
  }
};

const compressToSizeLimit = async (
  canvas: HTMLCanvasElement,
  draw: (w: number, h: number) => void,
  initialWidth: number,
  initialHeight: number,
): Promise<Blob> => {
  let width = initialWidth;
  let height = initialHeight;
  draw(width, height);
  let blob: Blob;

  // try same dimension lower quality
  for (const quality of QUALITY_STEPS) {
    blob = await canvasToBlob(canvas, quality);

    if (blob.size <= MAX_BYTES) break;

    // if quality reduced and still not broken also reduce dimension
    // if image size is getting too small, skip
    if (quality === QUALITY_STEPS[1] && Math.max(width, height) > 600) {
      const scale = 0.7;
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      draw(width, height);
    }
  }

  return blob!;
};

const canvasToBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('Canvas toBlob returned null')),
      'image/jpeg',
      quality,
    );
  });
};

const getDimensions = (source: HTMLImageElement | ImageBitmap): { height: number; width: number } => {
  // natural width/height of image if it exists, vs what css rules might make it in the UI
  if ('naturalWidth' in source) {
    return { width: source.naturalWidth, height: source.naturalHeight };
  }

  // width/height acts as real image size on ImageBitmap as fallback
  return { width: source.width, height: source.height };
};

const toJpegFile = async (file: File): Promise<File> => {
  const isHeicFile = await isHeic(file);
  if (!isHeicFile) return file;

  const blob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.9 });

  return new File([blob], file.name.replace(/\.[^.]+$/i, '.jpg'), { type: 'image/jpeg' });
};

export const prepareImageForUpload = async (file: File): Promise<File> => {
  const jpegFile = await toJpegFile(file);
  const source = await loadBitmap(jpegFile);

  try {
    const { width: srcW, height: srcH } = getDimensions(source);

    const scale = Math.min(1, MAX_DIMENSION / Math.max(srcW, srcH));
    const width = Math.round(srcW * scale);
    const height = Math.round(srcH * scale);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return jpegFile;

    const draw = (w: number, h: number) => {
      canvas.width = w;
      canvas.height = h;
      ctx.drawImage(source as CanvasImageSource, 0, 0, w, h);
    };

    const blob = await compressToSizeLimit(canvas, draw, width, height);

    if (!blob) return jpegFile;

    const baseName = jpegFile.name.replace(/\.[^.]+$/i, '') || 'photo';

    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
  } catch {
    return jpegFile;
  } finally {
    if ('close' in source && typeof source.close === 'function') {
      source.close();
    }
  }
};
